import { Injectable } from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Router } from '@angular/router';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { catchError, map, of, switchMap } from 'rxjs';

import {
  VipDiscountListByCompanyResponse,
  ExistingVipDiscountsResponse,
  VipDiscountCreateResponse,
} from '@app/api/models';
import { DiscountsService, VipDiscountsService } from '@app/api/services';
import { MerchantActions } from '@merchant/store/merchant.slice';
import { SnackbarComponent } from '@shared/components/snackbar/snackbar.component';
import { paramConvert } from '@shared/helpers/service-helpers';
import { DialogService } from '@shared/services/dialog.service';

import { VipDiscountsSellingActions } from './vip-discounts-selling.slice';

@Injectable({ providedIn: 'root' })
export class VipDiscountsSellingEffects {
  saveDiscounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VipDiscountsSellingActions.saveDiscounts.trigger),
      switchMap((action) => {
        return this.vipDiscountService
          .createVipDiscount({
            body: paramConvert(action.request),
          })
          .pipe(
            map((response: VipDiscountCreateResponse) => {
              this.router.navigate([
                '/selling/prices/discounts/vip-discounts/products-and-partners',
              ]);
              this.dialogService.open({
                type: 'NOTICE',
                title: 'VIP_DISCOUNTS.NOTICE_MODAL.TITLE',
                content: 'VIP_DISCOUNTS.NOTICE_MODAL.CONTENT',
              });
              this.store.dispatch(
                MerchantActions.updatePartnersFilter.trigger({
                  filter: { searchText: '' },
                })
              );
              return VipDiscountsSellingActions.saveDiscounts.success({
                response,
              });
            }),
            catchError(() =>
              of(VipDiscountsSellingActions.saveDiscounts.failure())
            )
          );
      })
    )
  );

  loadDiscountsGiven$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VipDiscountsSellingActions.loadDiscountsGiven.trigger),
      switchMap((action) => {
        return this.vipDiscountService
          .getGivenVipDiscountLists({ page: 0, size: 1000 })
          .pipe(
            map((response: Array<VipDiscountListByCompanyResponse>) =>
              VipDiscountsSellingActions.loadDiscountsGiven.success({
                response,
              })
            ),
            catchError(() =>
              of(VipDiscountsSellingActions.loadDiscountsGiven.failure())
            )
          );
      })
    )
  );

  updateDiscount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VipDiscountsSellingActions.editDiscount.trigger),
      switchMap((action) => {
        return this.vipDiscountService.updateVipDiscount(action.request).pipe(
          map(() => {
            this.snackBar.openFromComponent(SnackbarComponent, {
              horizontalPosition: 'center',
              verticalPosition: 'bottom',
              duration: 3000,
              data: this.translateService.instant('SHARED.SAVE_SUCCESS'),
            });
            return VipDiscountsSellingActions.editDiscount.success({
              index: action.index,
            });
          }),
          catchError(() =>
            of(
              VipDiscountsSellingActions.editDiscount.failure({
                index: action.index,
              })
            )
          )
        );
      })
    )
  );

  findExistingDiscountCombinations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        VipDiscountsSellingActions.findExistingDiscountCombinations.trigger
      ),
      switchMap((action) =>
        this.vipDiscountService
          .findExistingVipDiscountCombinations({
            body: action.request,
          })
          .pipe(
            map((response: ExistingVipDiscountsResponse) => {
              return VipDiscountsSellingActions.findExistingDiscountCombinations.success(
                {
                  response: response,
                }
              );
            }),
            catchError(() =>
              of(
                VipDiscountsSellingActions.findExistingDiscountCombinations.failure()
              )
            )
          )
      )
    )
  );

  deleteDiscount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VipDiscountsSellingActions.deleteDiscount.trigger),
      switchMap((action) => {
        return this.discountService
          .deleteDiscounts({ body: { discountIdList: [action.discountId] } })
          .pipe(
            map(() => {
              this.snackBar.openFromComponent(SnackbarComponent, {
                horizontalPosition: 'center',
                verticalPosition: 'bottom',
                duration: 3000,
                data: this.translateService.instant('SHARED.DELETE_SUCCESS'),
              });
              return VipDiscountsSellingActions.deleteDiscount.success({
                index: action.index,
              });
            }),
            catchError(() =>
              of(VipDiscountsSellingActions.deleteDiscount.failure())
            )
          );
      })
    )
  );

  deleteSelectedDiscounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VipDiscountsSellingActions.deleteSelectedDiscounts.trigger),
      switchMap((action) => {
        return this.discountService
          .deleteDiscounts({
            body: { discountIdList: action.selectedDiscounts },
          })
          .pipe(
            map(() => {
              this.store.dispatch(
                VipDiscountsSellingActions.loadDiscountsGiven.trigger()
              );
              return VipDiscountsSellingActions.deleteSelectedDiscounts.success();
            }),
            catchError(() =>
              of(VipDiscountsSellingActions.deleteDiscount.failure())
            )
          );
      })
    )
  );

  constructor(
    private readonly actions$: Actions,
    private router: Router,
    private readonly vipDiscountService: VipDiscountsService,
    private readonly discountService: DiscountsService,
    private dialogService: DialogService,
    private readonly snackBar: MatSnackBar,
    private translateService: TranslateService,
    private store: Store
  ) {}
}
